// Iteration 082 -- Event Detail Tabs (blocks + polls)
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import type {
  EventDetailBlockPublic,
  EventPollPublic,
  EventBlockType,
  PollOption,
  PollType,
  EventTaskPublic,
} from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Plus, Trash2, Lock } from "lucide-react";

const TAB_ORDER: EventBlockType[] = ["location", "arrival", "travel", "accommodation", "clothing", "warmup", "streaming", "custom"];
const TAB_LABELS: Record<EventBlockType | "overview" | "polls" | "tasks", string> = {
  overview: "Overview",
  location: "Location",
  roster: "Roster",
  arrival: "Arrival",
  travel: "Travel",
  accommodation: "Stay",
  clothing: "Clothing",
  warmup: "Warm-up",
  streaming: "Stream",
  custom: "Info",
  polls: "Voting",
  tasks: "Tasks",
};

export default function EventTabsPage() {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const [blocks, setBlocks] = useState<EventDetailBlockPublic[]>([]);
  const [polls, setPolls] = useState<EventPollPublic[]>([]);
  const [tasks, setTasks] = useState<EventTaskPublic[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [canCreatePoll, setCanCreatePoll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activeTab = (tab ?? "overview") as EventBlockType | "overview" | "polls" | "tasks";

  const reload = useCallback(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([api.getEventDetailBlocks(id), api.getEventPolls(id), api.getEventTasks(id)])
      .then(([blocksRes, pollsRes, tasksRes]) => {
        setBlocks(blocksRes.blocks);
        setCanEdit(blocksRes._actions.can_edit);
        setPolls(pollsRes.polls);
        setCanCreatePoll(pollsRes._actions.can_create);
        setTasks(tasksRes.tasks);
      })
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => reload(), [reload]);

  const blocksByType = useMemo(() => {
    const map: Partial<Record<EventBlockType, EventDetailBlockPublic[]>> = {};
    for (const b of blocks) {
      if (!map[b.block_type]) map[b.block_type] = [];
      map[b.block_type]!.push(b);
    }
    return map;
  }, [blocks]);

  const visibleTabs = useMemo(() => {
    const tabs: Array<EventBlockType | "overview" | "polls" | "tasks"> = ["overview"];
    for (const t of TAB_ORDER) {
      if ((blocksByType[t]?.length ?? 0) > 0 || canEdit) tabs.push(t);
    }
    if (polls.length > 0 || canCreatePoll) tabs.push("polls");
    // Tasks tab: organisers always see it; parents see it only when there's
    // something to engage with (at least one open task).
    const hasOpenTasks = tasks.some((t) => t.status === "open");
    if (hasOpenTasks || canEdit) tabs.push("tasks");
    return tabs;
  }, [blocksByType, canEdit, polls.length, canCreatePoll, tasks]);

  if (loading) return <p data-page="event-tabs" data-state="loading">Loading…</p>;

  return (
    <div className="flex flex-col gap-4" data-page="event-tabs">
      <div className="flex items-center justify-between">
        <Link to={`/events/${id}`} className="text-sm text-muted-foreground hover:underline">
          ← Back to event
        </Link>
      </div>
      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      <nav className="flex flex-wrap gap-2 border-b" role="tablist">
        {visibleTabs.map((t) => (
          <Link
            key={t}
            to={`/events/${id}/tabs/${t}`}
            className={`px-3 py-2 text-sm ${activeTab === t ? "border-b-2 border-primary font-semibold" : "text-muted-foreground"}`}
            role="tab"
            data-tab={t}
          >
            {TAB_LABELS[t]}
          </Link>
        ))}
      </nav>

      {activeTab === "overview" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Overview</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Use the tabs above to view location, travel, clothing, warm-up, streaming, documents, or voting.
            </p>
          </CardContent>
        </Card>
      )}

      {activeTab === "polls" ? (
        <PollsTab polls={polls} canCreate={canCreatePoll} eventId={id!} onChange={reload} />
      ) : activeTab === "tasks" ? (
        <TasksTab tasks={tasks} canEdit={canEdit} eventId={id!} onChange={reload} />
      ) : activeTab !== "overview" && (
        <BlocksTab
          eventId={id!}
          type={activeTab as EventBlockType}
          blocks={blocksByType[activeTab as EventBlockType] ?? []}
          canEdit={canEdit}
          onChange={reload}
        />
      )}
    </div>
  );
}

function BlocksTab(props: {
  eventId: string;
  type: EventBlockType;
  blocks: EventDetailBlockPublic[];
  canEdit: boolean;
  onChange: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.createEventDetailBlock(props.eventId, {
        block_type: props.type,
        title: title.trim() || TAB_LABELS[props.type],
        body: body.trim() || null,
        meta: {},
      });
      setTitle("");
      setBody("");
      setAdding(false);
      props.onChange();
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function remove(blockId: string) {
    if (!confirm("Remove this block?")) return;
    await api.deleteEventDetailBlock(props.eventId, blockId);
    props.onChange();
  }

  return (
    <div className="space-y-3">
      {props.blocks.length === 0 && !adding && (
        <p className="text-sm text-muted-foreground">No {TAB_LABELS[props.type].toLowerCase()} details yet.</p>
      )}

      {props.blocks.map((b) => (
        <Card key={b.id}>
          <CardHeader className="flex flex-row items-start justify-between">
            <CardTitle className="text-base">{b.title}</CardTitle>
            {props.canEdit && (
              <Button variant="ghost" size="sm" onClick={() => remove(b.id)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {b.body ? <p className="whitespace-pre-wrap text-sm">{b.body}</p> : <p className="text-sm text-muted-foreground">--</p>}
            {Object.keys(b.meta).length > 0 && (
              <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                {Object.entries(b.meta).map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd>{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </CardContent>
        </Card>
      ))}

      {props.canEdit && !adding && (
        <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add {TAB_LABELS[props.type]}
        </Button>
      )}

      {adding && (
        <Card>
          <CardContent className="space-y-2 pt-4">
            <input
              className="w-full rounded border px-3 py-2 text-sm"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <textarea
              className="w-full rounded border px-3 py-2 text-sm"
              rows={4}
              placeholder="Details (markdown)"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={save} disabled={saving}>Save</Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PollsTab(props: { polls: EventPollPublic[]; canCreate: boolean; eventId: string; onChange: () => void }) {
  const [creating, setCreating] = useState(false);
  const [question, setQuestion] = useState("");
  const [pollType, setPollType] = useState<PollType>("yes_no");
  const [optsText, setOptsText] = useState("Yes\nNo");

  async function save() {
    const options: PollOption[] = optsText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((label) => ({ label, value: label.toLowerCase().replace(/[^a-z0-9]+/g, "-") }));
    if (options.length < 2) {
      alert("Need at least 2 options");
      return;
    }
    try {
      await api.createEventPoll(props.eventId, { question, poll_type: pollType, options });
      setCreating(false);
      setQuestion("");
      props.onChange();
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed");
    }
  }

  return (
    <div className="space-y-3">
      {props.polls.length === 0 && <p className="text-sm text-muted-foreground">No polls yet.</p>}
      {props.polls.map((p) => (
        <PollCard key={p.id} poll={p} onChange={props.onChange} />
      ))}
      {props.canCreate && !creating && (
        <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Poll
        </Button>
      )}
      {creating && (
        <Card>
          <CardContent className="space-y-2 pt-4">
            <input
              className="w-full rounded border px-3 py-2 text-sm"
              placeholder="Question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
            <select
              className="w-full rounded border px-3 py-2 text-sm"
              value={pollType}
              onChange={(e) => setPollType(e.target.value as PollType)}
            >
              <option value="yes_no">Yes / No</option>
              <option value="single_choice">Single choice</option>
              <option value="multi_choice">Multi choice</option>
              <option value="volunteer_signup">Volunteer sign-up</option>
            </select>
            <textarea
              className="w-full rounded border px-3 py-2 text-sm"
              rows={4}
              placeholder={"One option per line"}
              value={optsText}
              onChange={(e) => setOptsText(e.target.value)}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={save}>Create</Button>
              <Button size="sm" variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PollCard(props: { poll: EventPollPublic; onChange: () => void }) {
  const p = props.poll;
  async function vote(option: string) {
    try {
      await api.respondToPoll(p.id, { option_value: option });
      props.onChange();
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed");
    }
  }
  async function close() {
    if (!confirm("Close this poll?")) return;
    await api.closeEventPoll(p.id);
    props.onChange();
  }
  const closed = p.closed_at !== null;
  return (
    <Card data-poll={p.id}>
      <CardHeader className="flex flex-row items-start justify-between">
        <CardTitle className="text-base">{p.question}</CardTitle>
        {p._actions.can_close && !closed && (
          <Button variant="ghost" size="sm" onClick={close}>
            <Lock className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {p.tally.map((t) => {
            const selected = p.my_votes.includes(t.value);
            const full = t.max_signups != null && t.count >= t.max_signups;
            return (
              <li key={t.value} className="flex items-center justify-between">
                <span className="text-sm">
                  <span className={selected ? "font-semibold" : ""}>{t.label}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {t.count}{t.max_signups != null ? `/${t.max_signups}` : ""}
                  </span>
                </span>
                {p._actions.can_respond && !full && !selected && (
                  <Button size="sm" variant="outline" onClick={() => vote(t.value)}>
                    Vote
                  </Button>
                )}
                {selected && <span className="text-xs text-green-600">✓ voted</span>}
              </li>
            );
          })}
        </ul>
        {closed && <p className="mt-2 text-xs text-muted-foreground">Poll closed.</p>}
      </CardContent>
    </Card>
  );
}

// 097 -- Tasks tab. Organisers create + track; parents claim + unclaim.
function TasksTab({
  tasks,
  canEdit,
  eventId,
  onChange,
}: { tasks: EventTaskPublic[]; canEdit: boolean; eventId: string; onChange: () => void }) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<{ title: string; description: string; category: string; needed_count: number }>({
    title: "",
    description: "",
    category: "general",
    needed_count: 1,
  });
  const [err, setErr] = useState<string | null>(null);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      await api.createEventTask(eventId, {
        title: draft.title,
        description: draft.description || null,
        category: draft.category as EventTaskPublic["category"],
        needed_count: draft.needed_count,
      });
      setDraft({ title: "", description: "", category: "general", needed_count: 1 });
      setCreating(false);
      onChange();
    } catch (e) {
      setErr(e instanceof api.ApiError ? e.message : "Failed to create task");
    }
  }

  const visibleTasks = canEdit
    ? tasks
    : tasks.filter((t) => t.status === "open");

  return (
    <div className="flex flex-col gap-4" data-region="tasks">
      {err && <p className="text-sm text-destructive" data-error>{err}</p>}
      {canEdit && !creating && (
        <div>
          <Button size="sm" onClick={() => setCreating(true)} data-action="new-task">
            <Plus className="mr-1 h-4 w-4" /> New task
          </Button>
        </div>
      )}
      {canEdit && creating && (
        <Card data-region="new-task-form">
          <CardHeader><CardTitle className="text-base">New task</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="flex flex-col gap-3">
              <div>
                <label htmlFor="task-title" className="text-xs font-medium">Title</label>
                <input
                  id="task-title"
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  required
                  data-field="title"
                />
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="task-category" className="text-xs font-medium">Category</label>
                  <select
                    id="task-category"
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={draft.category}
                    onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    data-field="category"
                  >
                    <option value="general">General</option>
                    <option value="transport">Transport</option>
                    <option value="catering">Catering</option>
                    <option value="scoring">Scoring</option>
                    <option value="setup">Setup</option>
                    <option value="first_aid">First aid</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="task-count" className="text-xs font-medium">Slots needed</label>
                  <input
                    id="task-count"
                    type="number"
                    min={1}
                    max={999}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={draft.needed_count}
                    onChange={(e) => setDraft({ ...draft, needed_count: Number(e.target.value) || 1 })}
                    data-field="needed_count"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="task-desc" className="text-xs font-medium">Description (optional)</label>
                <textarea
                  id="task-desc"
                  rows={3}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  title="Task description"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" data-action="save-task">Create</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
      {visibleTasks.length === 0 ? (
        <p className="text-sm text-muted-foreground">No tasks yet.</p>
      ) : (
        visibleTasks.map((t) => (
          <TaskCard key={t.id} task={t} canEdit={canEdit} onChange={onChange} />
        ))
      )}
    </div>
  );
}

function TaskCard({
  task,
  canEdit,
  onChange,
}: { task: EventTaskPublic; canEdit: boolean; onChange: () => void }) {
  const [userNotes, setUserNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const slotsRemaining = Math.max(0, task.needed_count - task.filled_count);

  async function handleClaim() {
    setBusy(true);
    setErr(null);
    try {
      await api.claimTask(task.id, { user_notes: userNotes || null });
      setUserNotes("");
      onChange();
    } catch (e) {
      setErr(e instanceof api.ApiError ? e.message : "Failed to claim");
    } finally {
      setBusy(false);
    }
  }

  async function handleUnclaim() {
    if (!task.my_assignment) return;
    if (!confirm("Unclaim this task? The slot will be offered to someone else.")) return;
    setBusy(true);
    try {
      await api.unclaimTask(task.my_assignment.id);
      onChange();
    } catch (e) {
      setErr(e instanceof api.ApiError ? e.message : "Failed to unclaim");
    } finally {
      setBusy(false);
    }
  }

  async function handleAssignmentTransition(assignmentId: string, to_status: string, requireNote: boolean) {
    let note: string | null = null;
    if (requireNote) {
      note = prompt("Note (required):");
      if (!note) return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.updateAssignmentStatus(assignmentId, {
        to_status: to_status as "accepted" | "completed" | "no_show" | "cancelled",
        organiser_notes: note,
      });
      onChange();
    } catch (e) {
      setErr(e instanceof api.ApiError ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteTask() {
    if (!confirm("Cancel this task? All outstanding assignments will be cancelled.")) return;
    setBusy(true);
    try {
      await api.cancelEventTask(task.id);
      onChange();
    } catch (e) {
      setErr(e instanceof api.ApiError ? e.message : "Failed to cancel");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card data-row={task.id} data-status={task.status}>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div className="flex-1">
          <CardTitle className="text-base">
            {task.title}
            {task.status === "cancelled" && <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">Cancelled</span>}
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {task.category} · {task.filled_count}/{task.needed_count} filled
            {task.starts_at_local && <> · {task.starts_at_local}{task.ends_at_local ? `-${task.ends_at_local}` : ""}</>}
          </p>
        </div>
        {canEdit && task.status === "open" && (
          <Button size="sm" variant="outline" onClick={handleDeleteTask} disabled={busy}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {task.description && (
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{task.description}</p>
        )}
        {err && <p className="text-sm text-destructive" data-error>{err}</p>}

        {/* Parent-side: claim / unclaim */}
        {task.status === "open" && !canEdit && (
          task.my_assignment && task.my_assignment.status !== "cancelled" ? (
            <div className="flex flex-wrap items-center gap-2 text-sm" data-region="my-assignment">
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-900">
                You're signed up · {task.my_assignment.status}
              </span>
              {task.my_assignment.status !== "completed" && task.my_assignment.status !== "no_show" && (
                <Button size="sm" variant="outline" onClick={handleUnclaim} disabled={busy} data-action="unclaim">Unclaim</Button>
              )}
            </div>
          ) : slotsRemaining > 0 ? (
            <div className="flex flex-col gap-2" data-region="claim-form">
              <input
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="Optional note (e.g. 'can fit 3 kids')"
                value={userNotes}
                onChange={(e) => setUserNotes(e.target.value)}
                data-field="user_notes"
              />
              <div>
                <Button size="sm" onClick={handleClaim} disabled={busy} data-action="claim">I'll help with this</Button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">All slots filled.</p>
          )
        )}

        {/* Organiser-side: assignments table */}
        {canEdit && task.assignments && task.assignments.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="pb-2">Volunteer</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2">Notes</th>
                  <th className="pb-2"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {task.assignments.map((a) => (
                  <tr key={a.id} data-row={a.id}>
                    <td className="py-2">{a.user_display_name}</td>
                    <td className="py-2" data-status={a.status}>{a.status}</td>
                    <td className="py-2 text-xs text-muted-foreground">
                      {a.user_notes && <>Parent: {a.user_notes}<br /></>}
                      {a.organiser_notes && <>Organiser: {a.organiser_notes}</>}
                    </td>
                    <td className="py-2 text-right">
                      {a.status === "pending" && (
                        <div className="flex flex-wrap justify-end gap-1">
                          <button type="button" className="text-xs text-emerald-700 hover:underline" onClick={() => handleAssignmentTransition(a.id, "accepted", false)} data-action="accept">Accept</button>
                          <button type="button" className="text-xs text-destructive hover:underline" onClick={() => handleAssignmentTransition(a.id, "cancelled", true)} data-action="cancel">Cancel</button>
                        </div>
                      )}
                      {a.status === "accepted" && (
                        <div className="flex flex-wrap justify-end gap-1">
                          <button type="button" className="text-xs text-emerald-700 hover:underline" onClick={() => handleAssignmentTransition(a.id, "completed", false)} data-action="complete">Complete</button>
                          <button type="button" className="text-xs text-amber-700 hover:underline" onClick={() => handleAssignmentTransition(a.id, "no_show", true)} data-action="no-show">No-show</button>
                          <button type="button" className="text-xs text-destructive hover:underline" onClick={() => handleAssignmentTransition(a.id, "cancelled", true)} data-action="cancel">Cancel</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {canEdit && task.assignments && task.assignments.length === 0 && (
          <p className="text-xs text-muted-foreground">No volunteers yet.</p>
        )}
      </CardContent>
    </Card>
  );
}
